import React, { useState, useEffect } from 'react';
import NavigationSidebar from '../../components/ui/NavigationSidebar';
import QuickActions from '../../components/ui/QuickActions';
import Button from '../../components/ui/Button';

import ClientTable from './components/ClientTable';
import ClientModal from './components/ClientModal';
import ClientStats from './components/ClientStats';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import ActivateConfirmModal from './components/ActivateConfirmModal';
import { logout } from '../../utils/auth';

const ClientManagement = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [clients, setClients] = useState([]);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activateTarget, setActivateTarget] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

   const handleClientSubmit = async (formData) => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));

      if (selectedClient) {
        // update existing
        setClients((prev) =>
          prev?.map((client) =>
            client?.id === selectedClient?.id
              ? { ...client, ...formData, updatedDate: new Date()?.toISOString() }
              : client
          )
        );
      } else {
        // add new
        const newClient = {
          id: Math.max(0, ...clients?.map((c) => c?.id)) + 1,
          ...formData,
          createdDate: new Date()?.toISOString(),
        };
        setClients((prev) => [...prev, newClient]);
      }

      setIsClientModalOpen(false);
      setSelectedClient(null);
    } catch (error) {
      console.error('Error saving client:', error);
    } finally {
      setIsLoading(false);
    }
  };

    // Logout handler
    const handleLogout = () => {
      logout();
      window.location.href = '/admin-login';
    };


  // Fetch clients
  useEffect(() => {
    const fetchClients = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${import.meta.env.VITE_AUTH_BASE_URL}/client/all`, {
          method: 'GET',
          headers: {
            accept: '*/*',
            Authorization: `Bearer ${token || ''}`,
          },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const formatted = Array.isArray(data)
          ? data.map((item, idx) => ({
              id: item?.client_id ?? idx + 1,
              name: item?.client_name || '-',
              email: item?.client_code || '-',
              clientName: item?.merchant_id || '-',
              status: (item?.status || '').toLowerCase(),
              role: '',
              createdDate: new Date().toISOString(),
              client_code: item?.client_code,
              merchant_id: item?.merchant_id,
            }))
          : [];

        setClients(formatted);
      } catch (err) {
        console.error('Error fetching clients:', err);
        setClients([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClients();
  }, []);

  // Add / Edit client
  const handleAddClient = () => {
    setSelectedClient(null);
    setIsClientModalOpen(true);
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setIsClientModalOpen(true);
  };

  // Delete client(s)
  const handleDeleteClient = (clientId) => {
    const client = clients?.find((c) => c?.id === clientId);
    setDeleteTarget({ type: 'single', client, id: clientId });
    setIsDeleteModalOpen(true);
  };

  const handleBulkDelete = (clientIds) => {
    setDeleteTarget({ type: 'multiple', ids: clientIds, count: clientIds?.length });
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('authToken');

      if (deleteTarget?.type === 'single') {
        const clientId = deleteTarget?.id;
        const res = await fetch(`${import.meta.env.VITE_AUTH_BASE_URL}/client/${clientId}/status`, {
          method: 'PUT',
          headers: {
            accept: '*/*',
            Authorization: `Bearer ${token || ''}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status_id: 0 }),
        });

        const result = await res.json();
        if (!res.ok || result?.status !== 'success') throw new Error(result?.message);

        setClients((prev) =>
          prev?.map((client) =>
            client?.id === clientId ? { ...client, status: 'in_process' } : client
          )
        );
      } else if (deleteTarget?.type === 'multiple') {
        const updatedClients = [...clients];
        for (const clientId of deleteTarget?.ids || []) {
          const res = await fetch(`${import.meta.env.VITE_AUTH_BASE_URL}/client/${clientId}/status`, {
            method: 'PUT',
            headers: {
              accept: '*/*',
              Authorization: `Bearer ${token || ''}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ status_id: 0 }),
          });

          const result = await res.json();
          if (res.ok && result?.status === 'success') {
            const index = updatedClients.findIndex((c) => c?.id === clientId);
            if (index > -1) updatedClients[index] = { ...updatedClients[index], status: 'in_process' };
          }
        }
        setClients(updatedClients);
      }

      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting client(s):', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Activate client
  const handleActivateClient = (clientId) => {
    const client = clients?.find((c) => c?.id === clientId);
    setActivateTarget({ type: 'single', client, id: clientId });
    setIsActivateModalOpen(true);
  };

  const handleActivateConfirm = async () => {
    if (!activateTarget) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const clientId = activateTarget?.id;

      const res = await fetch(`${import.meta.env.VITE_AUTH_BASE_URL}/client/${clientId}/status`, {
        method: 'PUT',
        headers: {
          accept: '*/*',
          Authorization: `Bearer ${token || ''}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status_id: 1 }), // activate
      });

      const result = await res.json();
      if (!res.ok || result?.status !== 'success') throw new Error(result?.message);

      setClients((prev) =>
        prev?.map((client) =>
          client?.id === clientId ? { ...client, status: 'active' } : client
        )
      );

      setIsActivateModalOpen(false);
      setActivateTarget(null);
    } catch (error) {
      console.error('Error activating client:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Export
  const handleExport = () => {
    const csvContent = [
      ['S.No', 'Client Name', 'Client Code', 'Status', 'Merchant ID'],
      ...clients?.map((client, index) => [
        index + 1,
        client?.name ?? '',
        client?.client_code ?? client?.email ?? '',
        client?.status ?? '',
        client?.clientName ?? client?.merchant_id ?? '',
      ]),
    ]
      ?.map((row) => row?.join(','))
      ?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    window.URL?.revokeObjectURL(url);
  };

  // Sort
  const handleSort = (key) => {
    const keyMap = { name: 'name', email: 'email', createdDate: 'createdDate', id: 'id', status: 'status' };
    const mappedKey = keyMap[key] || key;
    setSortConfig((prev) => ({
      key: mappedKey,
      direction: prev?.key === mappedKey && prev?.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const quickActions = [
    { id: 'add-client', label: 'Add New Client', icon: 'UserPlus', variant: 'default', onClick: handleAddClient },
    { id: 'export', label: 'Export Excel', icon: 'Download', variant: 'outline', onClick: handleExport },
  ];

  return (
    <div className="min-h-screen bg-background">
      <NavigationSidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        userRole="admin"
        isAuthenticated={true}
        onLogout={handleLogout}
      />
      <main className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-80'}`}>
        <div className="bg-card border-b border-border">
          <div className="px-6 py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Menu"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="lg:hidden"
                />
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Client Management</h1>
                  <p className="text-muted-foreground mt-1">
                    Manage developer accounts, API access permissions, and monitor client activity
                  </p>
                </div>
              </div>
              <QuickActions actions={quickActions} primaryActions={quickActions} showMoreMenu={false} />
            </div>
          </div>
        </div>

        <div className="p-6 space-y-8">
          <ClientStats clients={clients} />
          <div className="bg-card border border-border rounded-lg">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">All Clients</h2>
                  <p className="text-sm text-muted-foreground mt-1">Manage and monitor all registered clients</p>
                </div>
                <div className="text-sm text-muted-foreground">{clients?.length} total clients</div>
              </div>
            </div>
            <div className="p-6">
              <ClientTable
                clients={clients}
                onEdit={handleEditClient}
                onDelete={handleDeleteClient}
                onBulkDelete={handleBulkDelete}
                onExport={handleExport}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                roleFilter={roleFilter}
                onRoleFilterChange={setRoleFilter}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                sortConfig={sortConfig}
                onSort={handleSort}
                onActivate={handleActivateClient} // 🔹 Added activate handler
              />
              {isLoading && <div className="mt-4 text-sm text-muted-foreground">Loading clients from server...</div>}
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      <ClientModal
        isOpen={isClientModalOpen}
        onClose={() => {
          setIsClientModalOpen(false);
          setSelectedClient(null);
        }}
        onSubmit={handleClientSubmit}
        client={selectedClient}
        isLoading={isLoading}
      />
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        isLoading={isLoading}
        clientName={deleteTarget?.client?.name || ''}
        isMultiple={deleteTarget?.type === 'multiple'}
        count={deleteTarget?.count || 0}
      />
      <ActivateConfirmModal
        isOpen={isActivateModalOpen}
        onClose={() => {
          setIsActivateModalOpen(false);
          setActivateTarget(null);
        }}
        onConfirm={handleActivateConfirm}
        isLoading={isLoading}
        clientName={activateTarget?.client?.name || ''}
      />
    </div>
  );
};

export default ClientManagement;
